/**
 * Floating Form Button Module
 * Creates a floating button that appears when Gravity Forms are scrolled out of view
 * and scrolls back to the form when clicked
 */

/**
 * Throttle function to limit how often a function can be called
 * @param {Function} func - The function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
function throttle(func, limit) {
    let inThrottle;
    return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Check if a form wrapper is visible in the viewport
 * @param {HTMLElement} formWrapper - The form wrapper element
 * @returns {boolean} - True if form is visible
 */
function isFormVisible(formWrapper) {
    const rect = formWrapper.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < viewportHeight && rect.bottom > 0;
}

export default function initFloatingFormButton() {
    const forms = document.querySelectorAll('.gform_wrapper');

    if (!forms.length) {
        return;
    }

    // Create the floating button
    const button = document.createElement('button');
    button.className = 'gform-floating-button gform-floating-button--hidden';
    button.type = 'button';
    button.textContent = (window.novigranFseL10n && window.novigranFseL10n.floatingFormButton) || 'Ir a la reserva';

    // Allow the content (e.g. a promo post) to override the label
    const labelSource = forms[0].closest('[data-floating-button-label]');
    if (labelSource) {
        button.textContent = labelSource.dataset.floatingButtonLabel;
    }

    // Find page footer .mobile-tab-navigation
    const pageFooter = document.querySelector('.mobile-tab-navigation');

    if (pageFooter) {
        pageFooter.after(button);
    } else {
        document.body.appendChild(button);
    }

    // Handle button click - scroll to form
    button.addEventListener('click', function(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const targetForm = forms[0].querySelector('form');

        if (!targetForm) {
            return;
        }

        const header = document.getElementById('page-header');
        const headerHeight = header ? header.offsetHeight : 0;
        const formTop = targetForm.getBoundingClientRect().top + window.scrollY;
        const scrollTarget = formTop - headerHeight - 100;

        // Smooth scroll to form
        window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth'
        });
    });

    /**
     * Update button visibility state based on form visibility
     */
    function updateButtonState() {
        let isAnyHidden = false;

        forms.forEach(function(formWrapper) {
            if (!isFormVisible(formWrapper)) {
                isAnyHidden = true;
            }
        });

        if (isAnyHidden) {
            button.classList.remove('gform-floating-button--hidden');
            button.classList.add('gform-floating-button--visible');
        } else {
            button.classList.remove('gform-floating-button--visible');
            button.classList.add('gform-floating-button--hidden');
        }
    }

    /**
     * Update button position relative to footer
     */
    function updateButtonPosition() {
        if (!pageFooter) {
            return;
        }

        const footerElement = pageFooter;
        let footerRect = footerElement.getBoundingClientRect();
        const navElement = pageFooter.querySelector('nav.user-menu');

        if (footerRect.height === 0 && navElement) {
            footerRect = navElement.getBoundingClientRect();
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        let bottomOffset = 20;

        if (footerRect.top < viewportHeight) {
            const distanceToFooterTop = viewportHeight - footerRect.top;
            bottomOffset = Math.max(20, distanceToFooterTop + 20);
        }

        button.style.setProperty('--gform-floating-button-bottom', bottomOffset + 'px');
    }

    /**
     * Combined update function for throttling
     */
    function updateButton() {
        updateButtonState();
        updateButtonPosition();
    }

    // Throttled update function
    const throttledUpdate = throttle(updateButton, 100);

    // Listen to scroll and resize events
    window.addEventListener('scroll', throttledUpdate, { passive: true });
    window.addEventListener('resize', throttledUpdate, { passive: true });

    // Initial update
    updateButton();
}

